import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorFallback } from './ErrorFallback';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
  resetKeys?: unknown[];
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

function haveResetKeysChanged(prev?: unknown[], next?: unknown[]) {
  if (prev === next) return false;
  if (!prev || !next || prev.length !== next.length) return true;
  return prev.some((key, index) => !Object.is(key, next[index]));
}

/**
 * ErrorBoundary — catches render errors in its subtree and shows a friendly
 * fallback instead of crashing the app. Pass `resetKeys` (e.g. a route id) to
 * recover automatically when the boundary's context changes.
 *
 * Must be a class: React error boundaries rely on `getDerivedStateFromError`
 * and `componentDidCatch`, which have no functional/hook equivalent.
 *
 * @example
 * <ErrorBoundary resetKeys={[pipelineId]}>
 *   <Canvas />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { hasError } = this.state;
    const { resetKeys } = this.props;

    if (hasError && haveResetKeysChanged(prevProps.resetKeys, resetKeys)) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError } = this.props;
    onError?.(error, errorInfo);
  }

  render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      return fallback ?? <ErrorFallback />;
    }

    return children;
  }
}
